/**
 * ตัวช่วยตั้งค่าระบบ (Setup Wizard) — "ตั้งค่าจบในหน้าเดียว"
 * Owner กรอก config หลักทั้งหมด inline ที่นี่เลย ไม่ต้องเด้งไปหลายหน้า:
 * ข้อมูลบริษัท · รอบเงินเดือน · เวลาทำงาน · พิกัดเช็คอิน · การแจ้งเตือน
 * แต่ละหมวดมีปุ่มบันทึกในตัว. งานที่ต้องทำรายคน (พนักงาน/เงินเดือน/สิทธิ์ลา)
 * เป็นการ์ดทางลัด. จบแล้วดู "พิมพ์เขียว" ตรวจทานทั้งระบบ.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, CalendarClock, Clock, MapPin, Bell, Crosshair, Check,
  Users, Wallet, Plane, Sparkles, ArrowRight, CheckCircle2,
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { apiFetch } from '../../lib/api-client';
import {
  useSettings, useUpdateSettings,
  useCompanyProfile, useUpdateCompanyProfile,
  useSetupChecklist,
} from '../../hooks';

/** สวิตช์เปิด/ปิด (ทอง = เปิด) */
function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-11 h-6 rounded-full transition shrink-0 ${on ? 'bg-gold' : 'bg-gray-300'}`}
      aria-pressed={on}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition ${on ? 'translate-x-5' : ''}`} />
    </button>
  );
}

type FieldDef = {
  key: string; label: string;
  type: 'number' | 'text' | 'time' | 'toggle';
  hint?: string; step?: string; min?: string; max?: string;
};

const CYCLE_FIELDS: FieldDef[] = [
  { key: 'CUTOFF_DAY', label: 'วันปิดงวดเงินเดือน', type: 'number', min: '1', max: '31', hint: 'ตัดรอบทุกวันที่นี้ของเดือน (1–31)' },
  { key: 'PAYDAY_DAY', label: 'วันจ่ายเงินเดือน', type: 'number', min: '1', max: '31', hint: 'พิมพ์บนสลิป — เว้นว่างได้' },
  { key: 'SALARY_ONLY', label: 'โหมดเงินเดือนล้วน', type: 'toggle', hint: 'เปิด = ไม่ใช้ลงเวลา จ่ายเต็มจากฐานเงินเดือน (ไม่หักสาย/ขาด ไม่จ่าย OT)' },
];

const TIME_FIELDS: FieldDef[] = [
  { key: 'WORKDAYS_PER_WEEK', label: 'วันทำงานต่อสัปดาห์', type: 'number', min: '1', max: '7', hint: '1–7' },
  { key: 'WEEKEND_DAYS', label: 'วันหยุดประจำสัปดาห์', type: 'text', hint: 'เลขวัน 1=จันทร์…7=อาทิตย์ คั่นด้วย , เช่น 6,7' },
  { key: 'HOURS_PER_WORKDAY', label: 'ชั่วโมงทำงานต่อวัน', type: 'number', step: '0.25', min: '1', max: '24' },
  { key: 'LATE_GRACE_MINUTES', label: 'สายได้ไม่เกิน (นาที)', type: 'number', min: '0', max: '240' },
  { key: 'OT_WEEKLY_CAP_HOURS', label: 'เพดาน OT ต่อสัปดาห์ (ชม.)', type: 'number', step: '0.25', min: '0', max: '168', hint: 'กฎไทยทั่วไป 36 ชม./สัปดาห์' },
  { key: 'WORK_MORNING_IN_TIME', label: 'เวลาเข้างาน', type: 'time' },
  { key: 'WORK_EVENING_OUT_TIME', label: 'เวลาเลิกงาน', type: 'time' },
];

const GEO_FIELDS: FieldDef[] = [
  { key: 'GEOFENCE_ENABLED', label: 'ตรวจพิกัดตอนเช็คอิน', type: 'toggle', hint: 'ปิด = เช็คอินจากที่ไหนก็ได้ (เหมาะงานนอกสถานที่/หลายสาขา)' },
  { key: 'OFFICE_RADIUS_METERS', label: 'รัศมีที่อนุญาต (เมตร)', type: 'number', min: '20', max: '5000', hint: 'เริ่มต้น 200' },
];

const COMPANY_FIELDS: Array<{ key: string; label: string; hint?: string }> = [
  { key: 'company_name', label: 'ชื่อบริษัท (ตามหนังสือรับรอง)', hint: 'เช่น บริษัท โคตรมหาเฮง168 จำกัด' },
  { key: 'tax_id', label: 'เลขผู้เสียภาษี / นิติบุคคล (13 หลัก)', hint: 'แสดงบนสลิป + 50 ทวิ' },
  { key: 'company_phone', label: 'โทรศัพท์' },
  { key: 'company_address', label: 'ที่อยู่บริษัท', hint: 'ที่อยู่เต็มสำหรับเอกสาร' },
  { key: 'signer_name', label: 'ผู้มีอำนาจลงนาม', hint: 'เว้นว่าง = สลิปไม่ต้องลงนาม' },
  { key: 'signer_title', label: 'ตำแหน่งผู้ลงนาม', hint: 'เช่น กรรมการผู้จัดการ' },
];

const NOTIF_ITEMS: Array<{ key: string; label: string }> = [
  { key: 'checkin_scan', label: 'เตือนสแกนเข้า–ออกงาน' },
  { key: 'cutoff_reminder', label: 'เตือนใกล้ปิดรอบ / คำขอค้าง' },
  { key: 'approval_escalation', label: 'เร่งคำขอ/ลาที่ค้างเกินกำหนด' },
  { key: 'profile_followup', label: 'ตามให้พนักงานกรอกประวัติ' },
  { key: 'clockout_prompt', label: 'เตือนลืมสแกนออกงาน' },
  { key: 'briefing', label: 'สรุปงานวันนี้ (ทุกเช้า)' },
];

/** การ์ดหมวด — หัวข้อ + ไอคอน + คำอธิบาย + เนื้อหา + ปุ่มบันทึก */
function Section({
  icon: Icon, title, why, children, onSave, saving,
}: {
  icon: typeof Building2; title: string; why: string;
  children: ReactNode; onSave?: () => void; saving?: boolean;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-start gap-3 mb-4">
        <span className="w-9 h-9 rounded-xl bg-gold-wash text-gold-deep grid place-items-center shrink-0">
          <Icon className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-ink">{title}</h3>
          <p className="text-xs text-warm-gray mt-0.5">{why}</p>
        </div>
      </div>
      <div className="space-y-3">{children}</div>
      {onSave && (
        <div className="flex justify-end mt-4">
          <button onClick={onSave} disabled={saving} className="btn-cta inline-flex items-center gap-1.5 text-sm">
            <Check className="w-4 h-4" /> {saving ? 'กำลังบันทึก…' : 'บันทึกหมวดนี้'}
          </button>
        </div>
      )}
    </div>
  );
}

export function OwnerSetup() {
  const toast = useToast();
  const { data: checklist } = useSetupChecklist();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const profile = useCompanyProfile();
  const updateProfile = useUpdateCompanyProfile();

  // draft รวมของ settings (string) + company profile
  const [s, setS] = useState<Record<string, string>>({});
  const [c, setC] = useState<Record<string, string>>({});
  const [notif, setNotif] = useState<Record<string, boolean> | null>(null);
  const [gpsBusy, setGpsBusy] = useState(false);

  useEffect(() => { if (settings.data) setS((p) => ({ ...settings.data, ...p })); }, [settings.data]);
  useEffect(() => {
    if (profile.data) {
      const { logo_path, logo_url, ...rest } = profile.data;
      void logo_path; void logo_url;
      setC((p) => ({ ...(rest as Record<string, string>), ...p }));
    }
  }, [profile.data]);
  useEffect(() => {
    apiFetch<{ prefs: Record<string, boolean> }>('/owner/notification-settings')
      .then((r) => setNotif(r.prefs)).catch(() => setNotif({}));
  }, []);

  const setField = (k: string, v: string) => setS((p) => ({ ...p, [k]: v }));

  async function saveSettings(keys: string[], label: string) {
    const body: Record<string, string> = {};
    for (const k of keys) body[k] = s[k] ?? '';
    try { await updateSettings.mutateAsync(body); toast.success(`บันทึก${label}แล้ว`); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }
  async function saveCompany() {
    try { await updateProfile.mutateAsync(c); toast.success('บันทึกข้อมูลบริษัทแล้ว'); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }
  async function saveNotif() {
    if (!notif) return;
    try { await apiFetch('/owner/notification-settings', { method: 'POST', body: { prefs: notif } }); toast.success('บันทึกการแจ้งเตือนแล้ว'); }
    catch (e) { toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'); }
  }

  function locateGps() {
    if (!navigator.geolocation) { toast.error('อุปกรณ์ไม่รองรับ GPS'); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setField('OFFICE_LATITUDE', pos.coords.latitude.toFixed(6));
        setField('OFFICE_LONGITUDE', pos.coords.longitude.toFixed(6));
        setGpsBusy(false);
        toast.success('ดึงพิกัดปัจจุบันแล้ว — อย่าลืมกดบันทึก');
      },
      () => { setGpsBusy(false); toast.error('ดึงพิกัดไม่สำเร็จ — เปิดสิทธิ์ตำแหน่งแล้วลองใหม่'); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  function renderField(f: FieldDef) {
    if (f.type === 'toggle') {
      const on = (s[f.key] ?? '') === 'true';
      return (
        <div key={f.key} className="flex items-start justify-between gap-3 py-1">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">{f.label}</p>
            {f.hint && <p className="text-xs text-warm-gray mt-0.5">{f.hint}</p>}
          </div>
          <Toggle on={on} onClick={() => setField(f.key, on ? 'false' : 'true')} />
        </div>
      );
    }
    return (
      <div key={f.key}>
        <label className="label">{f.label}</label>
        <input
          type={f.type} step={f.step} min={f.min} max={f.max}
          className={`input ${f.type === 'number' ? 'num' : ''}`}
          value={s[f.key] ?? ''}
          onChange={(e) => setField(f.key, e.target.value)}
        />
        {f.hint && <p className="text-xs text-warm-gray mt-1">{f.hint}</p>}
      </div>
    );
  }

  const pct = checklist?.pct ?? 0;
  const geoOn = (s.GEOFENCE_ENABLED ?? '') === 'true';

  return (
    <AdminLayout title="ตัวช่วยตั้งค่าระบบ">
      <div className="max-w-2xl mx-auto space-y-4 pb-12">
        <div className="card p-5 bg-gradient-to-br from-paper to-gold-wash">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold text-ink">ตั้งค่าระบบให้จบในหน้าเดียว</h2>
              <p className="text-sm text-warm-gray mt-1">กรอกแต่ละหมวดแล้วกดบันทึกได้เลย ไม่ต้องเปิดหลายหน้า</p>
            </div>
            <div className="text-right shrink-0">
              <div className="num text-2xl font-semibold text-gold-deep">{pct}%</div>
              <div className="text-[11px] text-warm-gray">ความคืบหน้า</div>
            </div>
          </div>
        </div>

        {(settings.isLoading || profile.isLoading) && (
          <div className="card p-6 text-center text-muted">กำลังโหลด…</div>
        )}

        {/* 1. ข้อมูลบริษัท */}
        <Section icon={Building2} title="ข้อมูลบริษัท" why="ใช้พิมพ์บนสลิป + เอกสารภาษี (50 ทวิ)" onSave={saveCompany} saving={updateProfile.isPending}>
          {COMPANY_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="label">{f.label}</label>
              <input className="input" value={c[f.key] ?? ''} onChange={(e) => setC((p) => ({ ...p, [f.key]: e.target.value }))} />
              {f.hint && <p className="text-xs text-warm-gray mt-1">{f.hint}</p>}
            </div>
          ))}
        </Section>

        {/* 2. รอบเงินเดือน */}
        <Section icon={CalendarClock} title="รอบเงินเดือน" why="ระบบใช้คิดวันตัดรอบ การลา และการปิดงวด" onSave={() => saveSettings(CYCLE_FIELDS.map((f) => f.key), 'รอบเงินเดือน')} saving={updateSettings.isPending}>
          {CYCLE_FIELDS.map(renderField)}
        </Section>

        {/* 3. เวลาทำงาน */}
        <Section icon={Clock} title="เวลาทำงาน" why="ฐานคำนวณเวลาเข้างาน สาย และ OT" onSave={() => saveSettings(TIME_FIELDS.map((f) => f.key), 'เวลาทำงาน')} saving={updateSettings.isPending}>
          <div className="grid grid-cols-2 gap-3">{TIME_FIELDS.map(renderField)}</div>
        </Section>

        {/* 4. พิกัดเช็คอิน */}
        <Section icon={MapPin} title="พิกัดเช็คอิน (Geofence)" why="จำกัดให้สแกนเข้างานได้เฉพาะในพื้นที่ออฟฟิศ" onSave={() => saveSettings(['GEOFENCE_ENABLED', 'OFFICE_LATITUDE', 'OFFICE_LONGITUDE', 'OFFICE_RADIUS_METERS'], 'พิกัดเช็คอิน')} saving={updateSettings.isPending}>
          {renderField(GEO_FIELDS[0]!)}
          {geoOn && (
            <>
              <button type="button" onClick={locateGps} disabled={gpsBusy} className="btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm">
                <Crosshair className="w-4 h-4" /> {gpsBusy ? 'กำลังดึงพิกัด…' : 'ใช้ตำแหน่งปัจจุบันของฉัน'}
              </button>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Latitude</label>
                  <input className="input num" value={s.OFFICE_LATITUDE ?? ''} onChange={(e) => setField('OFFICE_LATITUDE', e.target.value)} placeholder="13.7563" />
                </div>
                <div>
                  <label className="label">Longitude</label>
                  <input className="input num" value={s.OFFICE_LONGITUDE ?? ''} onChange={(e) => setField('OFFICE_LONGITUDE', e.target.value)} placeholder="100.5018" />
                </div>
              </div>
              {renderField(GEO_FIELDS[1]!)}
            </>
          )}
        </Section>

        {/* 5. การแจ้งเตือน */}
        <Section icon={Bell} title="การแจ้งเตือน (LINE)" why="เลือกว่าจะให้ระบบเตือนอะไรเข้า LINE บ้าง (ปรับได้ตลอด)" onSave={saveNotif} saving={false}>
          {notif === null ? (
            <p className="text-sm text-muted">กำลังโหลด…</p>
          ) : (
            NOTIF_ITEMS.map((it) => (
              <div key={it.key} className="flex items-center justify-between gap-3 py-1">
                <p className="text-sm text-ink">{it.label}</p>
                <Toggle on={!!notif[it.key]} onClick={() => setNotif((p) => ({ ...(p ?? {}), [it.key]: !p?.[it.key] }))} />
              </div>
            ))
          )}
        </Section>

        {/* งานรายคน — เปิดหน้าเฉพาะ (ใส่ทีละคน/นำเข้า) */}
        <div className="card p-5">
          <h3 className="font-display font-semibold text-ink mb-1">พนักงาน · เงินเดือน · สิทธิ์การลา</h3>
          <p className="text-xs text-warm-gray mb-3">ส่วนที่ต้องใส่รายคน — เปิดหน้าเฉพาะเพื่อเพิ่ม/นำเข้า แล้วกลับมาที่นี่</p>
          <div className="space-y-2">
            {[
              { to: '/hr/people#onboard', icon: Users, label: 'เพิ่ม / นำเข้าพนักงาน' },
              { to: '/owner/comp', icon: Wallet, label: 'ตั้งฐานเงินเดือน (โครงสร้างค่าตอบแทน)' },
              { to: '/hr/time#leave', icon: Plane, label: 'ตั้งสิทธิ์การลา (ป่วย/กิจ/พักร้อน)' },
            ].map((x) => (
              <Link key={x.to} to={x.to} className="flex items-center gap-3 p-3 rounded-xl border border-[color:rgba(13,11,10,.10)] hover:border-gold transition">
                <span className="w-8 h-8 rounded-lg bg-gold-wash text-gold-deep grid place-items-center shrink-0"><x.icon className="w-4 h-4" /></span>
                <span className="text-sm font-medium text-ink flex-1">{x.label}</span>
                <ArrowRight className="w-4 h-4 text-muted" />
              </Link>
            ))}
          </div>
        </div>

        {/* จบ → พิมพ์เขียว */}
        <div className="card p-5 text-center space-y-3 bg-gradient-to-br from-paper to-gold-wash">
          {checklist?.complete
            ? <CheckCircle2 className="w-8 h-8 text-[#2f7d52] mx-auto" />
            : <Sparkles className="w-8 h-8 text-gold-deep mx-auto" />}
          <p className="font-display font-semibold text-ink">{checklist?.complete ? 'ตั้งค่าหลักครบแล้ว' : 'ตรวจทานทั้งระบบ'}</p>
          <p className="text-sm text-warm-gray">ดู “พิมพ์เขียวการทำเงินเดือน” เพื่อเห็นว่าตั้งค่าอะไรไปแล้ว + ยังขาดอะไร</p>
          <Link to="/owner/blueprint" className="btn-cta w-full justify-center inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> ดูพิมพ์เขียว (ตรวจทาน)
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
}
